import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { CustomNavigationService } from '@uw/core/custom-navigation.service';
import { CampaignFacadeService } from '@uw/core/facades/campaign.facade.service';
import {
  CampaignInfo,
  CampaignPageContent,
  CampaignTypes,
} from '@uw/core/models/campaign.model';
import { AuthService } from '@uw/core/services/auth.service';
import { CampaignStateService } from '@uw/core/services/campaign-state.service';
import { CampaignService } from '@uw/core/services/campaign.service';
import { LayoutUtilsService } from '@uw/shared/services/layout-utils.service';
import { MenuService } from '@uw/themes/services/menu.service';
import { combineLatest, Observable, of, filter, map, switchMap, take, tap } from 'rxjs';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { ProgressBarComponent } from '../../../shared/components/progress-bar/progress-bar.component';
import { AsyncPipe, CurrencyPipe } from '@angular/common';

@Component({
    selector: 'uw-my-campaign',
    templateUrl: './my-campaign-page.component.html',
    styleUrls: ['./my-campaign-page.component.scss'],
    imports: [ButtonComponent, ProgressBarComponent, AsyncPipe, CurrencyPipe, TranslateModule]
})
export class MyCampaignPageComponent implements OnInit, OnDestroy {

  @Input()
  sideImgSrc!: string | null;

  @Input()
  sideImageAlt!: string | null;   

  showButton$: Observable<boolean>;
  campaignList: CampaignInfo[];
  currentCampaign: CampaignInfo;
  pageContent!: Map<string, CampaignPageContent>;
  pageTitle!: string | null;
  contentDetail!: string | null;

  donorUnitedWays: { name: string; percentage: string }[];

  campaign: CampaignInfo;

  readonly currentLanguage = this.translateService.currentLang;

  readonly donateButtonLable$ = this.campaignService.getPageContent(this.campaignStateService.getSelectedCampaign()?.campaignGuid, 'welcome').pipe(
    map(data => {

      let donateButtonLabel, tooltipText  = 'button.donate_now';

      if (data && data.size > 0) {
        const labelContent = this.getContent('DonateButtonLabel', data) || donateButtonLabel;
        donateButtonLabel = labelContent.replace(/<[^>]+>/g, '').trim();

        tooltipText = donateButtonLabel;

        if (donateButtonLabel.length > 17) {
          donateButtonLabel = tooltipText.slice(0, 17) + '...'; // 17 is the character limit to display button appropriately
        }
      }

      return {donateButtonLabel, tooltipText};
    })
  );

  readonly myCampaignDetails$ = this.campaignService
    .getMyCampaignDetails(
      this.campaignStateService.getSelectedCampaign().campaignGuid
    )
    .pipe(
      map((r) => {
        const cd = {
          ...r,
          participationPercentageProgressBarValue: this._getProgressBarWidth(+r.participationPercentage),
          pledgePercentProgressBarValue: this._getProgressBarWidth(+r.pledgePercent),
          pledgePercent: +r.pledgePercent > 100 ? '100' : `${r.pledgePercent}`,
          uwPledgePercent: +r.uwPledgePercent > 100 ? '100' : `${r.uwPledgePercent}`,
          uwPledgePercentProgressBarValue: this._getProgressBarWidth(+r.uwPledgePercent),
          groupDetails: r.groupDetails.map(detail => {
            return {
              ...detail,
              percentageProgressBarValue: this._getProgressBarWidth(+detail.percentage)
            }
          })           
        };

        return cd;
      })
    );

  constructor(
    private menuService: MenuService,
    private campaingFacadeService: CampaignFacadeService,
    private campaignStateService: CampaignStateService,
    private customNavigationService: CustomNavigationService,
    private router: Router,
    private campaignService: CampaignService,
    private authService: AuthService,
    private layoutUtilsService: LayoutUtilsService,
    private translateService: TranslateService
  ) {
    this.menuService.hideOnlyRightNav = true;
    this.menuService.showCampaignSpecificLogos$.next(true);
    this.showButton$ = combineLatest([
      this.campaingFacadeService.getCurrentCampaign(),
    ]).pipe(
      map(([campaign]) => {
        this.currentCampaign = campaign;
        if (campaign) return true;
        else {
          return false;
        }
      })
    );
  }

  ngOnInit(): void {
    this.campaign = this.campaignStateService.getSelectedCampaign();
    // this.campaingFacadeService.getCurrentCampaign().pipe(
    //   switchMap(campaign => this._campaignService.getPageContent(campaign.campaignGuid, 'linkpage1'))
    // ).subscribe(
    //   data => {
    //     this.pageContent = data;
    //     this.loadContent();
    //   }
    // )
  }

  ngOnDestroy(): void {
    this.menuService.hideOnlyRightNav = false;
  }

  onDonateNow() {
    const currenctCampaign = this.campaignStateService.getSelectedCampaign();

    if (currenctCampaign.campaignType === CampaignTypes.Regular) {
      if (!this.authService.getUser()) {

        // Inorder to prevent 2fa flow from breaking, commented out below logic
        // to navigate to sidebar login, and sending user to standalone login page.
        // Logic of how to navigate a user with regular campaign can be implemented in later phase.
        this.customNavigationService.navigate(['main/login']);


        return;
      }
    }

    if (!this.authService.getUser()) {
      this.customNavigationService.navigate(
        [
          '/donor',
          this.campaignStateService.getSelectedCampaign()?.campaignGuid,
          'donation',
        ],
        { queryParamsHandling: 'preserve' }
      );
      return;
    }
    this.campaignService
      .getAprDonationSummary()
      .pipe(
        switchMap((donationSummaryList) => {
          const selectedCampignGuid =
            this.campaignStateService.getSelectedCampaign()?.campaignGuid;
          const campaignSpecificAprs =
            donationSummaryList &&
            donationSummaryList.filter(
              (apr) => apr.campaignGuid == selectedCampignGuid
            );
          if (campaignSpecificAprs && campaignSpecificAprs.length > 0) {
            return this.showOtherDonationDialog().pipe(
              map((navigate) => ({ navigate }))
            );
          } else {
            return of({ navigate: true });
          }
        }),
        take(1)
      )
      .subscribe((result) => {
        if (result.navigate) {
          this.customNavigationService.navigate(
            [
              '/donor',
              this.campaignStateService.getSelectedCampaign()?.campaignGuid,
              'donation',
            ],
            { queryParamsHandling: 'preserve' }
          );
        }
      });
  }

  back() {
    this.customNavigationService.navigate(['/donor', this.currentCampaign.campaignGuid], {
      queryParamsHandling: 'preserve',
    });
    return;
  }

  showOtherDonationDialog() {
    const dialogRef = this.layoutUtilsService.customConfirmationElement(
      'welcome_page.make_additional_donation_dialog_title',
      'welcome_page.make_additional_donation_msg',
      '',
      'common.yes',
      true,
      'common.no'
    );

    // on confirm click
    dialogRef.componentInstance
      .confirmed()
      .pipe(
        filter((confirm) => confirm),
        tap((_) => {
          dialogRef.componentInstance.viewLoading = false;
          dialogRef.close('Dialog closed after confirmation');
        })
      )
      .subscribe();

    return dialogRef.afterClosed().pipe(
      map((result) => {
        return !!result;
      })
    );
  }

  getContent(key: string, pageContent: Map<string, CampaignPageContent>) {
    if (pageContent && pageContent.has(key)) {
      return pageContent.get(key)?.contentXML ?? null;
    }
    return null;
  }

  _getProgressBarWidth(metric: number) {
    return metric > 100 ? '100%' :
           metric == 0 ? '0%' :
           metric > 0 && metric < 9 ? '2.1rem' :
           metric >= 9 ? metric + '%' : '' 
  }

  
}
